import type { EvalVideo } from "@sitescene/types";

import { useState } from "react";

import { ApiError } from "@/api/client";
import { Button } from "@/components/ui/buttons/button";
import { Input } from "@/components/ui/input/input";
import { TextArea } from "@/components/ui/textarea/textarea";
import {
	useGoldVideoUrl,
	useSuggestGoldVideo,
	useUpdateGoldVideo,
} from "@/features/gold-videos/use-gold-videos";
import { GeneralModal } from "@/features/modals/general-modal";

interface GoldVideoEditModalProps {
	onClose: () => void;
	video: EvalVideo;
}

/**
 * Edit a gold standard video's fields beside the clip, so the ground truth is
 * written against what happens on screen. Suggest has Gemini fill the title,
 * ground truth and severity; nothing is saved until Save.
 */
export const GoldVideoEditModal = ({
	onClose,
	video,
}: GoldVideoEditModalProps) => {
	const playback = useGoldVideoUrl(video.id);
	const update = useUpdateGoldVideo(video.id);
	const suggest = useSuggestGoldVideo(video.id);
	// The element itself failing (object missing or still uploading), as
	// opposed to the URL request failing. Either way the form stays usable.
	const [playbackFailed, setPlaybackFailed] = useState(false);

	const [title, setTitle] = useState(video.title || video.filename);
	const [description, setDescription] = useState(video.description ?? "");
	const [severity, setSeverity] = useState(
		video.severity != null ? String(video.severity) : "",
	);
	const [sceneDescription, setSceneDescription] = useState(
		video.sceneDescription ?? "",
	);
	const [sceneGeometry, setSceneGeometry] = useState(video.sceneGeometry ?? "");
	const [startTime, setStartTime] = useState(toLocalInput(video.startTime));

	const severityNumber = Number(severity);
	const severityValid =
		severity === "" ||
		(Number.isInteger(severityNumber) &&
			severityNumber >= 1 &&
			severityNumber <= 10);
	const busy = update.isPending || suggest.isPending;

	const handleSuggest = () => {
		suggest.mutate(undefined, {
			onSuccess: (suggestion) => {
				setTitle(suggestion.title);
				setDescription(suggestion.description);
				setSeverity(String(suggestion.severity));
			},
		});
	};

	const handleSave = () => {
		update.mutate(
			{
				description: description.trim(),
				sceneDescription: sceneDescription.trim(),
				sceneGeometry: sceneGeometry.trim(),
				severity: severity === "" ? undefined : severityNumber,
				startTime:
					startTime === "" ? undefined : new Date(startTime).toISOString(),
				title: title.trim() || video.filename,
			},
			{ onSuccess: onClose },
		);
	};

	const error = update.error ?? suggest.error;

	return (
		<GeneralModal
			closeOnSubmit={false}
			footer={
				<div className="flex w-full items-center justify-between gap-2">
					<Button
						color="secondary"
						isDisabled={busy}
						onClick={handleSuggest}
						size="sm"
					>
						{suggest.isPending ? "Watching the clip…" : "Suggest with Gemini"}
					</Button>
					<div className="flex gap-2">
						<Button color="secondary" onClick={onClose} size="sm">
							Cancel
						</Button>
						<Button
							isDisabled={busy || !severityValid}
							onClick={handleSave}
							size="sm"
						>
							{update.isPending ? "Saving…" : "Save"}
						</Button>
					</div>
				</div>
			}
			isOpen
			onClose={onClose}
			onSubmit={handleSave}
			panelClassName="sm:max-w-2xl"
			title={video.title || video.filename}
		>
			<div className="flex aspect-video w-full items-center justify-center overflow-hidden rounded-lg bg-gray-900">
				{playback.data && !playbackFailed ? (
					<video
						className="h-full w-full"
						controls
						onError={() => setPlaybackFailed(true)}
						preload="metadata"
						src={playback.data}
					/>
				) : (
					<p className="px-6 text-center text-sm text-gray-400">
						{playback.isLoading
							? "Loading the clip…"
							: "The clip can't play. It may still be uploading."}
					</p>
				)}
			</div>

			<Input label="Title" onChange={setTitle} value={title} />

			<TextArea
				hint="What actually happens in the clip, written by hand. Evals are measured against it."
				label="Ground truth"
				onChange={setDescription}
				rows={3}
				value={description}
			/>

			<div className="flex gap-4">
				<Input
					className="flex-1"
					label="Start time"
					onChange={setStartTime}
					type="datetime-local"
					value={startTime}
				/>
				<Input
					className="w-32"
					errorMessage={severityValid ? undefined : "1 to 10"}
					isInvalid={!severityValid}
					label="Severity (1–10)"
					onChange={setSeverity}
					type="number"
					value={severity}
				/>
			</div>

			<TextArea
				hint="The source camera's permanent scene. Copied from the camera when a clip is added from an event."
				label="Camera scene description"
				onChange={setSceneDescription}
				rows={3}
				value={sceneDescription}
			/>

			<TextArea
				hint="How the source camera is mounted and pointed."
				label="Camera geometry"
				onChange={setSceneGeometry}
				rows={2}
				value={sceneGeometry}
			/>

			{error && (
				<p
					className={
						error instanceof ApiError && error.statusCode === 409
							? "text-sm text-warning-primary"
							: "text-sm text-error-primary"
					}
					role="alert"
				>
					{error.message}
				</p>
			)}
		</GeneralModal>
	);
};

/** A date to a `datetime-local` input value, in local time to the minute. */
const toLocalInput = (value?: Date | string): string => {
	if (!value) return "";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "";
	const pad = (n: number) => String(n).padStart(2, "0");
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};
